import { FormEvent, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import { ErrorBlockText, TextInput } from '@/components/widget-components'
import { PaytrieAPI } from '@paytrie/client'
import { useSignupStore } from '@/reducers'
import {
  trackOnboardingFieldValidationFailed,
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'

interface ValidationErrors {
  howDidYouHear?: string
  whoReferredYou?: string
  previousCryptoExperience?: string
}

export default function SignupAdditionalInformation2() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const additionalInfo = useSignupStore.use.additionalInfo()
  const setHowDidYouHear = useSignupStore.use.setHowDidYouHear()
  const setWhoReferredYou = useSignupStore.use.setWhoReferredYou()
  const setPreviousCryptoExperience =
    useSignupStore.use.setPreviousCryptoExperience()

  const goToNextState = useSignupStore.use.nextState()
  const [loading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [touched, setTouched] = useState<ValidationErrors>({})
  const [isFormValid, setIsFormValid] = useState(false)

  const validateForm = useCallback((): ValidationErrors => {
    const newErrors: ValidationErrors = {}

    if (!additionalInfo?.howDidYouHear?.trim()) {
      newErrors.howDidYouHear = t('additionalInfo.errors.howDidYouHearRequired')
    }

    if (!additionalInfo?.whoReferredYou?.trim()) {
      newErrors.whoReferredYou = t(
        'additionalInfo.errors.whoReferredYouRequired'
      )
    }

    if (!additionalInfo?.previousCryptoExperience?.trim()) {
      newErrors.previousCryptoExperience = t(
        'additionalInfo.errors.previousCryptoExperienceRequired'
      )
    }

    return newErrors
  }, [additionalInfo, t])

  const handleBlur = (field: keyof ValidationErrors) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const formErrors = validateForm()
    if (formErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: formErrors[field] }))
      trackOnboardingFieldValidationFailed(
        'referral_and_experience',
        field,
        'required'
      )
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Check form validity whenever additionalInfo changes
  useEffect(() => {
    const formErrors = validateForm()
    const hasErrors = Object.keys(formErrors).length > 0
    const allFieldsFilled =
      additionalInfo?.howDidYouHear?.trim() &&
      additionalInfo?.whoReferredYou?.trim() &&
      additionalInfo?.previousCryptoExperience?.trim()

    setIsFormValid(!hasErrors && !!allFieldsFilled)
  }, [additionalInfo, validateForm])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    trackOnboardingStepSubmitted('referral_and_experience')
    setIsLoading(true)
    setErrorMessage('')

    try {
      const howDidYouHear = additionalInfo?.howDidYouHear?.trim()
      const whoReferredYou = additionalInfo?.whoReferredYou?.trim()
      const previousCryptoExperience =
        additionalInfo?.previousCryptoExperience?.trim()

      if (!howDidYouHear || !whoReferredYou || !previousCryptoExperience) {
        setErrorMessage(t('additionalInfo.error.generic'))
        trackOnboardingStepFailed('referral_and_experience', 'validation_error')
        return
      }

      const res = await PaytrieAPI.updateCurrentUser({
        olderUserInfo: {
          howDidYouHear,
          whoReferredYou,
          previousCryptoExperience,
        },
        currentFlow:
          PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.ADDITIONAL_INFO_PAGE2,
      })

      if (res.status !== 200) {
        setErrorMessage(t('additionalInfo.error.generic'))
        trackOnboardingStepFailed(
          'referral_and_experience',
          'unable_to_process'
        )
        return
      }

      setErrorMessage('')
      goToNextState()
      trackOnboardingStepCompleted('referral_and_experience')
      navigate('/SignupFraudWarningFollowUp')
    } catch {
      setErrorMessage(t('additionalInfo.error.generic'))
      trackOnboardingStepFailed('referral_and_experience', 'generic_error')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    trackOnboardingStepViewed('referral_and_experience')
  }, [])

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={handleSubmit}
      data-testid="container-additional-information2-form"
    >
      <div className="text-lg font-normal uppercase">
        {t('additionalInfo.howDidYouHear.question')}
      </div>
      <TextInput
        placeholder={t('additionalInfo.howDidYouHear.placeholder')}
        value={additionalInfo?.howDidYouHear || ''}
        onChange={(e) => setHowDidYouHear(e.target.value)}
        onBlur={() => handleBlur('howDidYouHear')}
        data-testid="input-how-did-you-hear"
      />
      {touched.howDidYouHear && errors.howDidYouHear && (
        <ErrorBlockText data-testid="error-how-did-you-hear">
          {errors.howDidYouHear}
        </ErrorBlockText>
      )}

      <div className="text-lg font-normal uppercase">
        {t('additionalInfo.whoReferredYou.question')}
      </div>
      <TextInput
        placeholder={t('additionalInfo.whoReferredYou.placeholder')}
        value={additionalInfo?.whoReferredYou || ''}
        onChange={(e) => setWhoReferredYou(e.target.value)}
        onBlur={() => handleBlur('whoReferredYou')}
        data-testid="input-who-referred-you"
      />
      {touched.whoReferredYou && errors.whoReferredYou && (
        <ErrorBlockText data-testid="error-who-referred-you">
          {errors.whoReferredYou}
        </ErrorBlockText>
      )}

      <div className="text-lg font-normal uppercase">
        {t('additionalInfo.previousCrypto.question')}
      </div>
      <TextInput
        placeholder={t('additionalInfo.previousCrypto.placeholder')}
        value={additionalInfo?.previousCryptoExperience || ''}
        onChange={(e) => setPreviousCryptoExperience(e.target.value)}
        onBlur={() => handleBlur('previousCryptoExperience')}
        data-testid="input-previous-crypto-experience"
      />
      {touched.previousCryptoExperience && errors.previousCryptoExperience && (
        <ErrorBlockText data-testid="error-previous-crypto-experience">
          {errors.previousCryptoExperience}
        </ErrorBlockText>
      )}

      <ErrorBlockText data-testid="error-form-message">
        {errorMessage}
      </ErrorBlockText>

      <Button
        className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
        type="submit"
        disabled={loading || !isFormValid}
        data-testid="btn-continue"
      >
        {loading ? <LoadingIcon /> : t('additionalInfo.continue')}
      </Button>
    </form>
  )
}
